package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.asList
import org.w3c.files.File

private val uploader = Bem("admin-image-uploader")
private val field = Bem("admin-field")

/**
 * One image that has a purpose of its own, such as the title image of an act: a tile with the image, or a drop zone
 * while there is none. A new file replaces the image once it is uploaded; until then, and if it is refused or its
 * upload fails, the current image stays. The record's save sends [singleImageUuid].
 */
@Composable
fun AdminSingleImageField(
    id: String,
    label: String,
    images: EditableImages,
    imageUrl: (String) -> String,
    hint: String? = null,
    error: String? = null,
    /** Ignores dropped files, e.g. while the record is saved; a surrounding disabled `<fieldset>` covers the buttons. */
    disabled: Boolean = false,
    /** A logo, such as a sponsor's: shown whole on white, and called "Logo" in the drop zone. */
    logo: Boolean = false,
    optional: Boolean = false,
) {
    var dragOver by remember { mutableStateOf(false) }
    var editing by remember { mutableStateOf<String?>(null) }
    val messageId = "$id-message"
    // the newest tile is shown: the replacement while it uploads, then the only one
    val image = images.images.lastOrNull()

    // once a replacement is uploaded, the images before it go
    val uploadedLast = images.images.lastOrNull()?.takeIf { it.uuid != null }
    SideEffect {
        if (uploadedLast != null) {
            images.images.dropLast(1).forEach { images.remove(it.key) }
        }
    }

    fun replace(files: List<File>) {
        files.firstOrNull()?.let { images.add(listOf(it)) }
    }

    Div({
        classes(field.block)
        attr("role", "group")
        attr("aria-labelledby", "$id-label")
    }) {
        Span({
            classes(field.element("label").className)
            id("$id-label")
        }) {
            Text(label)
            if (optional) {
                Text(" ")
                Span({ classes(field.element("optional").className) }) { Text("(optional)") }
            }
        }

        Div({ classes(uploader.block) }) {
            if (image != null) {
                Div({
                    classes(uploader.element("image").className, uploader.element("image").modifier(if (logo) "logo" else "main"))
                    if (image.uuid == null) {
                        attr("aria-busy", "true")
                    }
                }) {
                    Img(image.previewUrl ?: imageUrl(image.uuid!!), attrs = {
                        classes(uploader.element("img").className)
                        alt(label)
                    })
                    // a logo has the sponsor's name for a description and is never cropped
                    if (!logo) {
                        image.uuid?.let { uuid ->
                            ImageDetailsButton("Bilddetails zu $label") { editing = uuid }
                            ImageBadges(main = false, missingDescription = !images.detailsOf(uuid).hasDescription)
                        }
                    }
                    if (image.uuid == null) {
                        Span({ classes("admin-visually-hidden") }) { Text("Wird hochgeladen …") }
                    }
                    Button({
                        type(ButtonType.Button)
                        classes(uploader.element("remove").className)
                        attr("aria-label", "$label entfernen")
                        onClick { images.images.forEach { images.remove(it.key) } }
                    }) { Span({ attr("aria-hidden", "true") }) { Text("×") } }
                }
            }

            Label(attrs = {
                classes(uploader.element("dropzone").className)
                attr("data-over", dragOver.toString())
                onDragEnter { event ->
                    if (event.dataTransfer?.types?.toList()?.contains("Files") == true) {
                        event.preventDefault()
                        dragOver = true
                    }
                }
                onDragOver { event ->
                    if (event.dataTransfer?.types?.toList()?.contains("Files") == true) {
                        event.preventDefault()
                        dragOver = true
                    }
                }
                onDragLeave { dragOver = false }
                onDrop { event ->
                    event.preventDefault()
                    dragOver = false
                    if (!disabled) {
                        event.dataTransfer?.files?.asList()?.let(::replace)
                    }
                }
            }) {
                val what = if (logo) "Logo" else "Bild"
                Text(if (image == null) "$what hierher ziehen oder auswählen" else "Anderes $what hierher ziehen oder auswählen")
                Input(InputType.File) {
                    classes(uploader.element("file-input").className)
                    if (disabled) {
                        attr("disabled", "")
                    }
                    attr("accept", images.extensions.joinToString(",") { ".$it" })
                    attr("aria-describedby", messageId)
                    onChange { event ->
                        event.target.files?.asList()?.let(::replace)
                        // the same file can be chosen again after removing it
                        event.target.value = ""
                    }
                }
            }
        }

        if (!logo) {
            ImageDetailsFor(id, images, editing, imageUrl) { editing = null }
        }

        val message = error ?: images.message ?: hint
        Span({
            classes(field.element(if (error != null || images.message != null) "error" else "hint").className)
            id(messageId)
            attr("role", "status")
        }) { message?.let { Text(it) } }
    }
}

/** The image an [AdminSingleImageField] holds: the newest uploaded one. */
val EditableImages.singleImageUuid: String? get() = uuids.lastOrNull()
